// Motore di calcolo puro per la partita IVA in regime forfettario.
//
// Estratto da js/forfettario.js (che ne era l'unica sede, mescolato al DOM) per
// due motivi: poterlo testare senza browser e poterlo riusare nel confronto tra
// regimi (js/confrontoRegimi.js), che deve mostrare gli stessi numeri della
// pagina forfettario.
//
// Nel forfettario il reddito non si determina sui costi reali: si applica al
// fatturato il coefficiente di redditivita' dell'ATECO. Sul reddito, al netto
// dei contributi deducibili, si paga un'imposta sostitutiva piatta (5% per i
// primi 5 anni se ricorrono i requisiti di nuova attivita', altrimenti 15%),
// che sostituisce IRPEF e addizionali. Niente IVA in fattura, niente IRAP.
import { calcolaContributiPrevidenziali } from './previdenza.js';

// Soglia di ricavi/compensi per restare nel forfettario (L. 197/2022).
export const SOGLIA_FORFETTARIO = 85000;
// Superata questa soglia l'uscita e' immediata, gia' dall'anno in corso.
export const SOGLIA_USCITA_IMMEDIATA = 100000;
// Tetto di reddito da lavoro dipendente/pensione per poter accedere al regime.
export const TETTO_REDDITO_DIPENDENTE = 35000;

// Il tetto dei 35.000 EUR guarda il reddito, non la RAL: si stima al netto dei
// contributi a carico del lavoratore, con lo stesso fattore usato dalla pagina
// forfettario storica.
export function redditoDipendenteAmmesso(ralDipendente) {
    const ral = parseFloat(ralDipendente);
    if (!Number.isFinite(ral)) return true;
    return (ral * 0.909) <= TETTO_REDDITO_DIPENDENTE;
}

// input: {
//   fatturato,
//   coefficienteRedditivita,        // in percentuale (es. 78)
//   aliquotaImposta,                // 5 o 15
//   gestione,                       // elemento di data/forfettario.json
//   mesiAttivita, nuovaAttivitaArtCom, riduzione50, riduzione35,
//   numeroMandanti, fasciaEtaEnpam,
//   dipendenteFullTime, riduzioneDipendente, ralDipendente
// }
export function calcolaForfettario(input) {
    const fatturato = Math.max(0, parseFloat(input.fatturato) || 0);
    const coefficiente = Math.max(0, parseFloat(input.coefficienteRedditivita) || 0);
    const aliquotaImposta = Math.max(0, parseFloat(input.aliquotaImposta) || 0);

    // Base imponibile previdenziale e fiscale: fatturato x coefficiente.
    const imponibileContributi = fatturato * (coefficiente / 100);

    // Il tetto dei 35.000 EUR e' un requisito di ACCESSO al forfettario (art. 1
    // c. 57 lett. d-ter L. 190/2014), non una regola contributiva: la riduzione
    // da soggetto gia' assicurato dipende solo dall'essere dipendente full-time
    // altrove, esattamente come nel regime ordinario (vedi js/previdenza.js).
    // Tenerli separati serve al confronto tra regimi, che simula il forfettario
    // anche per chi non puo' usarlo: legandoli, a quella simulazione veniva
    // addebitato un minimale Artigiani/Commercianti che quella persona non
    // pagherebbe mai, con netti negativi privi di senso.
    const dipendenteAmmesso = redditoDipendenteAmmesso(input.ralDipendente);

    const previdenza = calcolaContributiPrevidenziali(
        imponibileContributi,
        fatturato,
        input.gestione,
        {
            mesiAttivita: input.mesiAttivita,
            nuovaAttivitaArtCom: input.nuovaAttivitaArtCom,
            riduzione50: input.riduzione50,
            riduzione35: input.riduzione35,
            numeroMandanti: input.numeroMandanti,
            fasciaEtaEnpam: input.fasciaEtaEnpam,
            dipendenteFullTime: input.dipendenteFullTime,
            riduzioneDipendente: Boolean(input.riduzioneDipendente)
        }
    );
    const { contributi, contributiDeducibili, contributoIntegrativo, integrativoRivalsa, contributiACarico } = previdenza;

    const imponibileImposta = Math.max(0, imponibileContributi - contributiDeducibili);
    const imposta = Math.max(0, Math.round(imponibileImposta * (aliquotaImposta / 100)));

    // Come nell'ordinario, l'integrativo di cassa e' ribaltato in fattura al
    // cliente: non riduce il netto.
    const nettoAnnuo = Math.round(fatturato - contributiACarico - imposta);

    return {
        fatturato,
        coefficienteRedditivita: coefficiente,
        aliquotaImposta,
        imponibileContributi: Math.round(imponibileContributi),
        contributi: Math.round(contributi),
        contributiDeducibili: Math.round(contributiDeducibili),
        contributoIntegrativo: Math.round(contributoIntegrativo),
        integrativoRivalsa: Math.round(integrativoRivalsa),
        contributiACarico: Math.round(contributiACarico),
        imponibileImposta: Math.round(imponibileImposta),
        imposta,
        // Nel forfettario l'imposta sostitutiva e' l'unico tributo: niente
        // addizionali. Il campo tiene la stessa forma del risultato ordinario.
        imposteTotali: imposta,
        nettoAnnuo,
        nettoMensile: Math.round(nettoAnnuo / 12),
        dipendenteAmmesso
    };
}
